import React, { useState, useEffect, useRef } from 'react';
import './messagecenter.scss';
import Sidebar from '../../components/sidebar/Sidebar';
import Navbar from '../../components/navbar/Navbar';
import { useAuth } from '../../context/AuthContext';
import { messageService } from '../../firebase/messageService';
import { autoCreateNotifications } from '../../firebase/notificationService';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
    collection,
    query,
    where,
    orderBy,
    onSnapshot,
    addDoc,
    getDocs,
    serverTimestamp
  } from 'firebase/firestore';
  import { db } from '../../firebase/config';
import { useParams } from 'react-router-dom';

const MessageCenter = () => {
  const { user } = useAuth();
  const { userId } = useParams();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({ admins: true, tenants: true });
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch all users (except current user) - everyone can chat with everyone
  useEffect(() => {
    if (!user) {
      console.log('User not set');
      return;
    }
    const fetchUsers = async () => {
      try {
        setLoading(true);
        // Get all users except the current user
        const usersQuery = query(
          collection(db, 'users'), 
          where('uid', '!=', user.uid)
        );
        const querySnapshot = await getDocs(usersQuery);
        const usersList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setUsers(usersList);
        // If userId param is present, set selectedUser to that user
        if (userId) {
          const foundUser = usersList.find(u => u.id === userId || u.uid === userId);
          if (foundUser) setSelectedUser(foundUser);
          else if (usersList.length > 0 && !selectedUser) setSelectedUser(usersList[0]);
        } else if (usersList.length > 0 && !selectedUser) setSelectedUser(usersList[0]);
        setLoading(false);
        console.log('Fetched users:', usersList);
      } catch (error) {
        console.error('Error fetching users:', error);
        setLoading(false);
      }
    };
    fetchUsers();
    // eslint-disable-next-line
  }, [user, userId]);

  // Fetch messages for the selected conversation
  useEffect(() => {
    if (!selectedUser || !user) {
      console.log('selectedUser or user not set');
      return;
    }
    const conversationId = [user.uid, selectedUser.id].sort().join('_');
    console.log('Fetching messages for conversationId:', conversationId);
    const messagesQuery = query(
      collection(db, 'messages'),
      where('conversationId', '==', conversationId),
      orderBy('timestamp', 'asc')
    );
    const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      const messagesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMessages(messagesList);
      console.log('Fetched messages:', messagesList);
    });
    return () => unsubscribe();
  }, [selectedUser, user]);
  
  // Send a message
  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedUser) return;
    
    const conversationId = [user.uid, selectedUser.id].sort().join('_');
    const messageData = {
      conversationId,
      senderId: user.uid,
      senderName: user.displayName || user.email,
      senderRole: user.role,
      receiverId: selectedUser.id,
      receiverName: selectedUser.displayName || selectedUser.email,
      receiverRole: selectedUser.role,
      content: newMessage.trim(),
      timestamp: serverTimestamp()
    };

    try {
      // Add message to database
      const messageRef = await addDoc(collection(db, 'messages'), messageData);
      console.log('Message sent successfully:', messageRef.id);
      
      // Create notification for the recipient
      const notificationResult = await autoCreateNotifications.onMessageSent({
        ...messageData,
        id: messageRef.id
      });
      
      if (notificationResult.success) {
        console.log('Notification created successfully');
      } else {
        console.error('Failed to create notification:', notificationResult.error);
      }
      
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      // You might want to show an error message to the user here
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const admins = users.filter(userItem => userItem.role === 'admin');
  const tenants = users.filter(userItem => userItem.role === 'tenant');

  const UserSection = ({ title, users, role, icon: Icon, sectionKey }) => (
    <div className="userSection">
      <div 
        className="sectionHeader" 
        onClick={() => toggleSection(sectionKey)}
      >
        <div className="sectionTitle">
          <Icon className="sectionIcon" />
          <span>{title}</span>
          <span className="userCount">({users.length})</span>
        </div>
        {expandedSections[sectionKey] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </div>
      {expandedSections[sectionKey] && (
        <div className="usersContainer">
          {users.map((userItem) => (
            <div
              key={userItem.id}
              className={`userItem ${selectedUser?.id === userItem.id ? 'active' : ''}`}
              onClick={() => setSelectedUser(userItem)}
            >
              <div className="userAvatar">
                <Icon />
              </div>
              <div className="userInfo">
                <span className="userName">{userItem.displayName || userItem.email}</span>
                <span className="userRole">{role}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="messageCenter">
        <Sidebar />
        <div className="messageCenterContainer">
          <Navbar />
          <div className="loading">
            <h2>Loading Message Center...</h2>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="messageCenter">
      <Sidebar />
      <div className="messageCenterContainer">
        <Navbar />
        <div className="messageCenterContent">
          {/* User List */}
          <div className="usersList">
            <h3>Message Center</h3>
            <UserSection 
              title="Administrators" 
              users={admins} 
              role="Administrator" 
              icon={AdminPanelSettingsIcon}
              sectionKey="admins"
            />
            <UserSection 
              title="Tenants" 
              users={tenants} 
              role="Tenant" 
              icon={PersonIcon}
              sectionKey="tenants"
            />
          </div>
          {/* Chat Area */}
          <div className="messagesArea">
            <div className="messagesHeader">
              <div className="selectedUserInfo">
                <div className="userAvatar">
                  {selectedUser.role === 'admin' ? <AdminPanelSettingsIcon /> : <PersonIcon />}
                </div>
                <div>
                  <h3>{selectedUser.displayName || selectedUser.email}</h3>
                  <span>{selectedUser.role === 'admin' ? 'Administrator' : 'Tenant'}</span>
                </div>
              </div>
            </div>
            <div className="messagesContainer">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`message ${message.senderId === user.uid ? 'sent' : 'received'}`}
                >
                  <div className="messageContent">
                    <p>{message.content}</p>
                    <span className="messageTime">{formatTime(message.timestamp)}</span>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
            <form className="messageInput" onSubmit={sendMessage}>
              <input
                type="text"
                placeholder="Type your message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
              />
              <button type="submit" disabled={!newMessage.trim()}>
                <SendIcon />
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageCenter; 